import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useBranchContext } from "@/contexts/BranchContext";
import { useDemo } from "@/contexts/DemoContext";
import { useDemoStoreOptional } from "@/contexts/DemoStoreContext";
import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert } from "@/integrations/supabase/types";
import { useAuditLog } from "@/hooks/useAuditLog";
import { useToast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/utils";
import { VEHICLE_BLOCKING_STATUSES, type BookingStatus } from "@/types/booking-status";

export interface BookingData {
  id: string;
  status: BookingStatus;
  start_date: string;
  end_date: string;
  daily_rate: number | null;
  total_price: number | null;
  notes: string | null;
  pickup_mileage: number | null;
  return_mileage: number | null;
  actual_return_date: string | null;
  signature_url: string | null;
  customer_id: string | null;
  vehicle_id: string | null;
  pickup_branch_id?: string | null;
  customer: {
    id: string;
    full_name: string;
    phone: string | null;
    email: string | null;
    id_document_path?: string | null;
    license_document_path?: string | null;
  } | null;
  vehicle: {
    id: string;
    make: string | null;
    model: string | null;
    plate_number: string;
    daily_rate: number | null;
    status?: string;
  } | null;
}

const PAGE_SIZE = 50;

type BookingMutation = Partial<BookingData> & {
  id?: string;
  customer_id?: string | null;
  vehicle_id?: string | null;
  pickup_branch_id?: string | null;
};
type BookingQueryRow = Tables<"bookings"> & {
  customers: BookingData["customer"];
  vehicles: BookingData["vehicle"];
  pickup_branch_id?: string | null;
};
type BookingInsertPayload = TablesInsert<"bookings"> & { pickup_branch_id?: string | null };

export function useBookings() {
  const { isDemo } = useDemo();
  const store = useDemoStoreOptional();
  const { profile } = useAuth();
  const { toast } = useToast();
  const { log } = useAuditLog();
  const { activeBranchId } = useBranchContext();
  const qc = useQueryClient();
  const [page, setPage] = useState(0);

  const query = useQuery({
    queryKey: ["bookings", profile?.business_id, activeBranchId, page],
    queryFn: async () => {
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let q = supabase
        .from("bookings")
        .select(
          "*, customers!bookings_customer_id_fkey(id, full_name, phone, email, id_document_path, license_document_path), vehicles!bookings_vehicle_id_fkey(id, make, model, plate_number, daily_rate, status)",
          { count: "exact" },
        )
        .eq("business_id", profile!.business_id)
        .order("created_at", { ascending: false })
        .range(from, to);

      if (activeBranchId) {
        q = q.eq("pickup_branch_id", activeBranchId);
      }

      const { data, error, count } = await q;
      if (error) throw error;

      const rows = (data ?? []) as BookingQueryRow[];
      const mapped = rows.map((booking): BookingData => ({
        id: booking.id,
        status: (booking.status ?? "inquiry") as BookingStatus,
        start_date: booking.start_date ?? "",
        end_date: booking.end_date ?? "",
        daily_rate: booking.daily_rate,
        total_price: booking.total_price,
        notes: booking.notes,
        pickup_mileage: booking.pickup_mileage,
        return_mileage: booking.return_mileage,
        actual_return_date: booking.actual_return_date,
        signature_url: booking.signature_url,
        customer_id: booking.customer_id,
        vehicle_id: booking.vehicle_id,
        pickup_branch_id: booking.pickup_branch_id ?? null,
        customer: booking.customers ?? null,
        vehicle: booking.vehicles ?? null,
      }));

      return { items: mapped, totalCount: count ?? 0 };
    },
    enabled: !isDemo && !!profile?.business_id,
  });

  const allBookings = isDemo
    ? ((store?.bookings ?? []) as BookingData[])
    : (query.data?.items ?? []);

  const bookings = isDemo && activeBranchId
    ? allBookings.filter((booking) => booking.pickup_branch_id === activeBranchId)
    : allBookings;

  const totalCount = isDemo ? bookings.length : (query.data?.totalCount ?? 0);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const upsertBooking = useMutation({
    mutationFn: async (booking: BookingMutation) => {
      if (isDemo && store) {
        return store.upsertBooking(booking);
      }

      const { id, customer, vehicle, ...rest } = booking;
      const payload = {
        ...rest,
        business_id: profile?.business_id ?? null,
        created_by: profile?.id ?? null,
      } as BookingInsertPayload;

      if (id) {
        const { data, error } = await supabase
          .from("bookings")
          .update(payload)
          .eq("id", id)
          .select()
          .single();

        if (error) throw error;
        return data as Tables<"bookings">;
      }

      if (payload.vehicle_id && payload.start_date && payload.end_date) {
        const { data: conflicts } = await supabase
          .from("bookings")
          .select("id")
          .eq("vehicle_id", payload.vehicle_id)
          .in("status", ["confirmed", ...VEHICLE_BLOCKING_STATUSES])
          .lt("start_date", payload.end_date)
          .gt("end_date", payload.start_date)
          .limit(1);

        if (conflicts && conflicts.length > 0) {
          throw new Error("This vehicle is already booked for the selected dates.");
        }
      }

      const { data, error } = await supabase
        .from("bookings")
        .insert(payload)
        .select()
        .single();

      if (error) throw error;
      return data as Tables<"bookings">;
    },
    onSuccess: (data, variables) => {
      if (!isDemo) {
        qc.invalidateQueries({ queryKey: ["bookings"] });
        qc.invalidateQueries({ queryKey: ["vehicles"] });
        qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
        log(variables.id ? "updated" : "created", "booking", data?.id ?? variables.id, variables);
      }
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const deleteBooking = useMutation({
    mutationFn: async (id: string) => {
      if (isDemo && store) {
        store.deleteBooking(id);
        return;
      }

      const { error } = await supabase.from("bookings").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, id) => {
      if (!isDemo) {
        qc.invalidateQueries({ queryKey: ["bookings"] });
        qc.invalidateQueries({ queryKey: ["vehicles"] });
        qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
        log("deleted", "booking", id);
      }
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: BookingStatus }) => {
      if (isDemo && store) {
        store.updateBookingStatus(id, status);
        return;
      }

      const { error } = await supabase.from("bookings").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      if (!isDemo) {
        qc.invalidateQueries({ queryKey: ["bookings"] });
        qc.invalidateQueries({ queryKey: ["vehicles"] });
        qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
        log("status_changed", "booking", variables.id, { status: variables.status });
      }
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  return {
    bookings,
    isLoading: !isDemo && query.isLoading,
    error: query.error,
    upsertBooking,
    deleteBooking,
    updateStatus,
    page,
    setPage,
    totalPages,
    totalCount,
  };
}
